import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setToken, clearToken, getToken } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = getToken();
    if (!t) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => clearToken())
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
  };
  const register = async (email, password, name, opts = {}) => {
    const payload = { email, password, name };
    if (typeof opts.sms_consent === "boolean") payload.sms_consent = opts.sms_consent;
    if (opts.phone) payload.phone = opts.phone;
    // Plan picked on /pricing or a state page → stored on the user so it survives verify-on-another-device.
    if (opts.plan) { payload.plan = opts.plan; payload.interval = opts.interval || "month"; if (opts.state) payload.state = opts.state; }
    const { data } = await api.post("/auth/register", payload);
    setToken(data.token);
    setUser(data.user);
    // Return the verify-send outcome so the signup screen can prompt the
    // user to check inbox / spam, or explain if the send failed.
    return {
      email_verification: data.email_verification || null,
      email_verification_error: data.email_verification_error || null,
    };
  };
  const logout = (next) => {
    clearToken(); setUser(null);
    // Optional `next` (e.g. "/portal?pay=<id>") survives the trip so a one-click email link resumes after re-login.
    window.location.href = next && next.startsWith("/") ? `/login?next=${encodeURIComponent(next)}` : "/login";
  };
  // Re-pull /auth/me — used right after email verification so the portal
  // banner + checkout gate reflect email_verified=true without a reload.
  const refresh = useCallback(async () => {
    if (!getToken()) return null;
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
